/**
 * RESPONSIBILITY: the controls a person chooses a repeating date with.
 *
 *   [Every week  v]  [x] Sun [ ] Mon [x] Tue …                     at [19:30] (optional)
 *   [Every month v]  [By day of the week v] [3rd v] [Friday v]     at [19:00]
 *   [Every month v]  [By date v]  [15 v]                           at [--:--]
 *   [Every year  v]  [December v] [25 v]                           at [--:--]
 *
 * It replaced a text box that wanted cron. Every choice is plain words. The seven days are
 * checkboxes because several may be ticked and all of them fit on a line; everything else
 * is one answer from a list, which is what a <select> is for.
 *
 * Shared by the section control and the @List cell, so the two cannot look different.
 * Day and month names come from Intl, in English like every other word in the editor.
 */
import { useEffect, useRef, useState } from 'react';
import { ORDINALS, readRepeat, type Ordinal, type RepeatFreq } from '@framework/core';
import { daysInMonth, draftOf, textOf, withFreq, type RepeatDraft } from './repeat-draft';

const name = (o: Intl.DateTimeFormatOptions, d: Date) => new Intl.DateTimeFormat('en', o).format(d);
const WEEKDAYS = [...Array(7).keys()].map((d) => name({ weekday: 'long' }, new Date(2023, 0, 1 + d)));
const SHORT_DAYS = [...Array(7).keys()].map((d) => name({ weekday: 'short' }, new Date(2023, 0, 1 + d)));
const MONTHS = [...Array(12).keys()].map((m) => name({ month: 'long' }, new Date(2023, m, 1)));
const dates = (n: number): [number, string][] => [...Array(n).keys()].map((i) => [i + 1, String(i + 1)]);

const FREQ_WORDS: [string, string][] = [
  ['', 'Does not repeat'], ['weekly', 'Every week'], ['monthly', 'Every month'], ['yearly', 'Every year'],
];
const ORDINAL_WORDS: Record<string, string> = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', '-1': 'Last' };

export interface RepeatPickerProps {
  value: string;
  onChange: (text: string) => void;
  /** Goes on the first control, which is what a caption's htmlFor points at. */
  id?: string;
}

/** A <select> whose empty first option means "not chosen yet". */
function Pick({ value, options, onPick, label }: {
  value: number | undefined; options: [number, string][];
  onPick: (n: number | undefined) => void; label: string;
}) {
  return (
    <select
      className="ctl-input" aria-label={label} value={value ?? ''}
      onChange={(e) => onPick(e.target.value === '' ? undefined : Number(e.target.value))}
    >
      <option value="">{label}…</option>
      {options.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
    </select>
  );
}

export function RepeatPicker({ value, onChange, id }: RepeatPickerProps) {
  const [d, setD] = useState<RepeatDraft>(() => draftOf(value));
  // What this picker last wrote. A value arriving that is not it came from outside — a
  // reload, a conflict resolved, another row — and replaces the draft.
  const wrote = useRef(value);
  useEffect(() => {
    if (value !== wrote.current) { wrote.current = value; setD(draftOf(value)); }
  }, [value]);

  const put = (next: RepeatDraft) => {
    setD(next);
    wrote.current = textOf(next);
    onChange(wrote.current);
  };
  const set = (patch: Partial<RepeatDraft>) => put({ ...d, ...patch });
  const toggleDay = (day: number) =>
    set({ days: d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day] });

  // Something is stored that this picker cannot show. Say so, and offer the way out —
  // otherwise it reads "Does not repeat" over a value that is refused on every save.
  if (value.trim() && !readRepeat(value)) {
    return (
      <div className="ctl-repeat">
        <span className="ctl-repeat-unread">Saved as “{value}”, which is not a repeat this site can read.</span>
        <button type="button" id={id} className="ctl-add" onClick={() => put(withFreq(d, ''))}>Clear it</button>
      </div>
    );
  }

  return (
    <div className="ctl-repeat">
      <select
        id={id} className="ctl-input" value={d.freq}
        onChange={(e) => put(withFreq(d, e.target.value as '' | RepeatFreq))}
      >
        {FREQ_WORDS.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
      </select>

      {d.freq === 'weekly' && (
        <div className="ctl-optgroup" role="group" aria-label="On these days">
          {SHORT_DAYS.map((w, day) => (
            <label key={day} className="ctl-opt">
              <input type="checkbox" checked={d.days.includes(day)} onChange={() => toggleDay(day)} />
              <span>{w}</span>
            </label>
          ))}
        </div>
      )}

      {d.freq === 'monthly' && (
        <select
          className="ctl-input" aria-label="How" value={d.monthly}
          onChange={(e) => set({ monthly: e.target.value as RepeatDraft['monthly'], nth: undefined, days: [], monthDay: undefined })}
        >
          <option value="weekday">By day of the week (e.g. 3rd Friday)</option>
          <option value="date">By date (e.g. the 15th)</option>
        </select>
      )}
      {d.freq === 'monthly' && d.monthly === 'weekday' && (
        <>
          <Pick
            label="Which" value={d.nth} options={ORDINALS.map((n) => [n, ORDINAL_WORDS[n]])}
            onPick={(n) => set({ nth: n as Ordinal | undefined })}
          />
          <Pick
            label="Day" value={d.days[0]} options={WEEKDAYS.map((w, i) => [i, w])}
            onPick={(n) => set({ days: n === undefined ? [] : [n] })}
          />
        </>
      )}
      {d.freq === 'monthly' && d.monthly === 'date' && (
        <Pick label="Date" value={d.monthDay} options={dates(31)} onPick={(n) => set({ monthDay: n })} />
      )}

      {d.freq === 'yearly' && (
        <>
          <Pick
            label="Month" value={d.month} options={MONTHS.map((w, i) => [i + 1, w])}
            // A date the new month does not have is dropped, not kept out of sight.
            onPick={(n) => set({ month: n, monthDay: (d.monthDay ?? 0) > daysInMonth(n) ? undefined : d.monthDay })}
          />
          <Pick label="Date" value={d.monthDay} options={dates(daysInMonth(d.month))} onPick={(n) => set({ monthDay: n })} />
        </>
      )}

      {d.freq && (
        <label className="ctl-repeat-time">
          <span>at</span>
          <input type="time" className="ctl-input" value={d.time} onChange={(e) => set({ time: e.target.value })} />
          <span className="ctl-repeat-optional">(optional)</span>
        </label>
      )}
      {d.freq === 'monthly' && d.monthly === 'date' && (d.monthDay ?? 0) > 28 && (
        <p className="ctl-repeat-note">Months without a {d.monthDay === 31 ? '31st' : `${d.monthDay}th`} are skipped.</p>
      )}
    </div>
  );
}
